import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, ReactNode } from 'react';
import { ErrorDialog } from '../components/ErrorDialog';
import { setErrorDialogGetter } from '../lib/notify';

interface ErrorDialogContextValue {
  showError: (message: string, details?: string) => void;
}

const ErrorDialogContext = createContext<ErrorDialogContextValue | undefined>(undefined);

export interface ErrorDialogProviderProps {
  children: ReactNode;
}

export function ErrorDialogProvider({ children }: ErrorDialogProviderProps) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [details, setDetails] = useState<string | undefined>(undefined);

  const showError = useCallback((errorMessage: string, errorDetails?: string) => {
    setMessage(errorMessage);
    setDetails(errorDetails);
    setOpen(true);
  }, []);

  const handleOpenChange = useCallback((newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      // Clear details when dialog closes
      setDetails(undefined);
    }
  }, []);

  const value: ErrorDialogContextValue = useMemo(
    () => ({
      showError,
    }),
    [showError]
  );

  // Register the error dialog getter so notifyError can access it
  useEffect(() => {
    setErrorDialogGetter(() => value);
    return () => {
      setErrorDialogGetter(null);
    };
  }, [value]);

  return (
    <ErrorDialogContext.Provider value={value}>
      {children}
      <ErrorDialog open={open} onOpenChange={handleOpenChange} message={message} details={details} />
    </ErrorDialogContext.Provider>
  );
}

export function useErrorDialog(): ErrorDialogContextValue {
  const context = useContext(ErrorDialogContext);
  if (!context) {
    // Fallback: return a no-op function if context is not available
    // This allows notifyError to work even if provider is missing
    return {
      showError: (message: string) => {
        console.error('[ErrorDialog] Context not available, logging to console:', message);
      },
    };
  }
  return context;
}

